import { Moment } from "moment-timezone";
import { useCallback, useMemo } from "react";
import { matchRoutes } from "react-router";
import type { ApolloLink, OperationVariables } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import isoCountries from "i18n-iso-countries";
import { AnyPeriod, castPeriod, useAppConfig } from "@ct-react/core";
import { useLocaleContext, useLocaleUrl } from "@ct-react/locale";
import { getRecaptchaToken } from "../bundles/client-ui/recaptcha";
import { pricing } from "../i18n/sharable-defs";
import { routesConfig } from "../routes";

type GTMEventPayload = {
  event: string;
  ecommerce?: never;
  [key: string]: unknown;
}

type GTMEcommerceEventPayload = {
  event: string;
  ecommerce: Record<string, unknown>;
  [key: string]: unknown;
}

export const useTools = () => {

  const { env, options: { recaptchaSiteKey } } = useAppConfig();
  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { currentUrl } = useLocaleUrl();

  const routingData = useMemo(() => (key: "search"): any | undefined => {
    const matches = matchRoutes(routesConfig(), currentUrl, /(\/[^\/]*)\//.exec(currentUrl)![1])
      ?.filter(r => !!r.route.data && key in r.route.data);
    if (!matches) return undefined;
    return matches.reduce((acc, cur) => ({...acc, ...cur.route.data![key]}), {});
  }, [ currentUrl ]);

  const nights = useCallback((period: AnyPeriod) => {
    const casted = castPeriod(period);
    return casted.end.diff(casted.start, "day");
  }, []);

  const countries = useMemo(() => Object.fromEntries(Object
    .entries(isoCountries.getNames(locale.basename, { select: "official" }))
    .sort((a, b) => a[1].localeCompare(b[1]))), [ locale ]);

  const labelizeCalendar = useMemo(() => ({
    month: (m: Moment) => intl.formatDate(m.toDate(), { month: "long", year: "numeric" }),
    dayOfWeek: (d: Moment) => intl.formatDate(d.toDate(), { weekday: "short" }),
    onlineRestricted: intl.formatMessage(pricing.onDemandShort)
  }), [ intl ]);

  const gtmSendEvent = useCallback((payload: GTMEventPayload | GTMEcommerceEventPayload): void => {
    if (env !== "production" || typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    "ecommerce" in payload && window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }, [ env ]);

  const withRecaptcha = <TData = any, TVariables extends OperationVariables = OperationVariables>(
      mutationFunction: (options?: useMutation.MutationFunctionOptions<TData, TVariables>) => Promise<ApolloLink.Result<TData>>,
      action?: string) => {
    return async (options?: useMutation.MutationFunctionOptions<TData, TVariables>): Promise<ApolloLink.Result<TData>> => {
      if (!recaptchaSiteKey)
        return mutationFunction(options);

      const token = await getRecaptchaToken(recaptchaSiteKey, action ? { action } : undefined);
      return mutationFunction({
        ...options,
        context: {
          ...options?.context,
          headers: {
            ...options?.context?.headers,
            "GQL-Consumer-Recaptcha-Token": token
          }
        }
      });
    };
  };

  return {
    routingData,
    format: { nights },
    countries,
    labelizeCalendar,
    mutation: { withRecaptcha },
    gtm: { sendEvent: gtmSendEvent }
  };

}
